import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { clearAllAuthData } from '../../utils/authUtils';
import { AUTH_ENDPOINTS, CLIENTE_ENDPOINTS } from '../../utils/apiConfig';
import './LoginPage.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true); // Initialize as true to show loading initially
  const [error, setError] = useState('');
  const { login, isAuthenticated, userRole } = useAuth();
  const location = useLocation(); // To detect navigation changes

  // Only clear simulation data but preserve actual user types when the login page loads
  useEffect(() => {
    localStorage.removeItem('simulatedUserType');
  }, []);

  // Auto-login effect - this will run both on initial render and when navigating to the page
  useEffect(() => {
    // This function attempts auto-login
    const attemptAutoLogin = async () => {
      const token = localStorage.getItem('token');
      const storedUserRole = localStorage.getItem('userRole');
      
      if (token && storedUserRole) {
        try {
          // API endpoint to validate token based on user role
          const endpoint = storedUserRole === 'user' 
            ? AUTH_ENDPOINTS.ME
            : CLIENTE_ENDPOINTS.ME;
          
          const response = await fetch(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            // If validation successful, use the existing token to login
            if (storedUserRole === 'user') {
              // Clean up any simulated user types
              localStorage.removeItem('simulatedUserType');
              login(token, 'user', userData.data);
            } else {
              login(token, 'cliente', userData.data);
            }
          } else {
            // Token is invalid or expired, clear localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('userRole');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('userData');
            localStorage.removeItem('simulatedUserType');
          }
        } catch (error) {
          console.error('Auto-login error:', error);
        }
      }
      
      // Always set loading to false after attempting auto-login
      setLoading(false);
    };
    
    // Always attempt auto-login when the component mounts or when the URL changes
    attemptAutoLogin();
  }, [login, location.pathname]); // Added location.pathname to dependencies
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor, preencha todos os campos.');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      // IMPORTANT: Clean up any existing user-related data in localStorage
      clearAllAuthData();
      
      // First, try to authenticate as a regular user
      const userResponse = await fetch(AUTH_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      const userData = await userResponse.json();
      
      if (userResponse.ok) {
        // Extract the permissions and determine user type
        if (userData.user && userData.user.permissions) {
          let userType = 'associado'; // Default
          if (userData.user.permissions.includes('Master')) {
            userType = 'master';
          } else if (userData.user.permissions.includes('Diretoria')) {
            userType = 'diretoria';
          }
          
          // Store the user type explicitly
          localStorage.setItem('userType', userType);
        }
        
        // Use the login function from AuthContext
        login(userData.token, 'user', userData.user);
        return;
      }
      
      // If user authentication failed, try as a client
      const clienteResponse = await fetch(CLIENTE_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      const clienteData = await clienteResponse.json();
      
      if (clienteResponse.ok) {
        // Use the login function from AuthContext
        login(clienteData.token, 'cliente', clienteData.data);
        return;
      }
      
      // If both authentication methods failed
      setError('Email ou senha inválidos.');
      setLoading(false);
      
    } catch (error) {
      console.error('Erro ao autenticar:', error);
      setError('Ocorreu um erro durante a autenticação. Tente novamente.');
      setLoading(false);
    }
  };
  
  return (
    <>
      <Header />
      
      <section className="login-section">
        <div className="login-container">
          {loading ? (
            <div className="loading-container">
              <h2>Autenticando...</h2>
              <p>Por favor, aguarde enquanto verificamos suas credenciais.</p>
            </div>
          ) : (
            <>
              <div className="login-header">
                <h2>Entrar</h2>
                <p>Faça login para acessar sua conta</p>
              </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu email"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Senha</label>
              <input 
                type="password" 
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                required
              />
            </div>
            
            <div className="forgot-password">
              <Link to="/recuperar-senha">Esqueceu sua senha?</Link>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button 
              type="submit" 
              className="login-button" 
              disabled={loading}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
          
          <div className="register-link">
            <p>Não tem uma conta? <Link to="/cadastro">Cadastre-se</Link></p>
          </div>
            </>
          )}
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default LoginPage;